import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './PassionProject.css';
import usePageTitle from '../hooks/usePageTitle';
import { useLanguage } from '../hooks/useLanguage';
import borderImage from '../assets/border.png';
import poseidonImage from '../assets/poseidon.png';
import arrowIcon from '../assets/arrow.svg';
import mothercare1 from '../assets/mothercare1.png';
import mothercare2 from '../assets/mothercare2.png';
import mentorarc from '../assets/mentorarc.png';
import paper1 from '../assets/paper1.png';
import paper2 from '../assets/paper2.png';
import cybersmart1 from '../assets/cybersmart1.png';
import cybersmart2 from '../assets/cybersmart2.png';

const PassionProject: React.FC = () => {
  const { t, language } = useLanguage();
  usePageTitle(t('passion.title'));
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const toggleItem = (index: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Remove animation classes first to reset
    const allElements = document.querySelectorAll('.passion-project-title, .passion-project-subtitle, .passion-project-description');
    allElements.forEach(element => {
      element.classList.remove('animate-in-initial');
    });
    
    // Force reflow to ensure the removal is processed
    void document.body.offsetHeight;
    
    // Animate initial content on page load
    // Title animates first
    const titleElement = document.querySelector('.passion-project-title');
    if (titleElement) {
      setTimeout(() => {
        titleElement.classList.add('animate-in-initial');
      }, 200);
    }
    
    // Subtitle animates after title
    const subtitleElement = document.querySelector('.passion-project-subtitle');
    if (subtitleElement) {
      setTimeout(() => {
        subtitleElement.classList.add('animate-in-initial');
      }, 210);
    }
    
    // Description animates after subtitle
    const descElement = document.querySelector('.passion-project-description');
    if (descElement) {
      setTimeout(() => {
        descElement.classList.add('animate-in-initial');
      }, 410);
    }
  }, []);

  return (
    <div className="passion-project-page">
      <div className="passion-project-container">
        <div className="passion-project-hero">
          <img src={poseidonImage} alt="Poseidon" className="poseidon-image" />
          <h1 className="passion-project-title">{language === 'en' ? <><em><strong>Mens et Manus</strong></em> - Passion Project</> : <>Mens et Manus-열정 프로젝트</>}</h1>
        </div>

        <div className="passion-project-content">
          <p className="passion-project-subtitle">
            {language === 'en' ? (
              <><em>Mens et Manus</em>—the union of intellect and purposeful creation. {t('passion.intro')}</>
            ) : (
              <><em>Mens et Manus</em>. {t('passion.subtitle').split('.').slice(1).join('.').trim()} {t('passion.intro')}</>
            )}
          </p>

          <img src={borderImage} alt="" className="passion-project-decorative-line" />
        </div>

        <div className="exempli-section">
          <h2 className="exempli-title">{t('passion.examplesTitle').split('.')[0]}</h2>

          <div className="exempli-list">
            <div className={`exempli-item mothercare-item ${expandedItems.has(0) ? 'expanded' : ''}`}>
              <div className="exempli-header" onClick={() => toggleItem(0)}>
                <span className="exempli-label">{t('passion.example1Title')}</span>
                <img src={arrowIcon} alt="Arrow" className={`exempli-arrow ${expandedItems.has(0) ? 'rotated' : ''}`} />
              </div>
              <div className={`exempli-content mothercare-content ${expandedItems.has(0) ? 'visible' : ''}`}>
                <div className="exempli-content-inner mothercare-inner">
                  <div className="exempli-images">
                    <img src={mothercare1} alt="Mothercare" className="exempli-img" />
                    <img src={mothercare2} alt="Mothercare" className="exempli-img" />
                  </div>
                  <div className="exempli-text mothercare-text">
                    <p>{t('passion.example1Short')}</p>
                    <Link to="/mothercare" className="learn-more-link"><em>{t('passion.readMore')}</em></Link>
                  </div>
                </div>
              </div>
            </div>

            <div className={`exempli-item mentorarc-item ${expandedItems.has(1) ? 'expanded' : ''}`}>
              <div className="exempli-header" onClick={() => toggleItem(1)}>
                <span className="exempli-label">{t('passion.example2Title')}</span>
                <img src={arrowIcon} alt="Arrow" className={`exempli-arrow ${expandedItems.has(1) ? 'rotated' : ''}`} />
              </div>
              <div className={`exempli-content mentorarc-content ${expandedItems.has(1) ? 'visible' : ''}`}>
                <div className="exempli-content-inner mentorarc-inner">
                  <div className="exempli-images">
                    <img src={mentorarc} alt="MentorArc" className="exempli-img" />
                  </div>
                  <div className="exempli-text mentorarc-text">
                    <p>{t('passion.example2Short')}</p>
                    <Link to="/mentorarc" className="learn-more-link"><em>{t('passion.readMore')}</em></Link>
                  </div>
                </div>
              </div>
            </div>

            <div className={`exempli-item siemens-item ${expandedItems.has(2) ? 'expanded' : ''}`}>
              <div className="exempli-header" onClick={() => toggleItem(2)}>
                <span className="exempli-label">{t('passion.example3Title')}</span>
                <img src={arrowIcon} alt="Arrow" className={`exempli-arrow ${expandedItems.has(2) ? 'rotated' : ''}`} />
              </div>
              <div className={`exempli-content siemens-content ${expandedItems.has(2) ? 'visible' : ''}`}>
                <div className="exempli-content-inner siemens-inner">
                  <div className="exempli-images">
                    <img src={paper1} alt="Siemens Research" className="exempli-img" />
                    <img src={paper2} alt="Siemens Research" className="exempli-img" />
                  </div>
                  <div className="exempli-text siemens-text">
                    <p>{t('passion.example3Short')}</p>
                    <Link to="/siemens" className="learn-more-link"><em>{t('passion.readMore')}</em></Link>
                  </div>
                </div>
              </div>
            </div>

            <div className={`exempli-item cybersmart-item ${expandedItems.has(3) ? 'expanded' : ''}`}>
              <div className="exempli-header" onClick={() => toggleItem(3)}>
                <span className="exempli-label">{t('passion.example4Title')}</span>
                <img src={arrowIcon} alt="Arrow" className={`exempli-arrow ${expandedItems.has(3) ? 'rotated' : ''}`} />
              </div>
              <div className={`exempli-content cybersmart-content ${expandedItems.has(3) ? 'visible' : ''}`}>
                <div className="exempli-content-inner cybersmart-inner">
                  <div className="exempli-images">
                    <img src={cybersmart1} alt="CyberSmart" className="exempli-img" />
                    <img src={cybersmart2} alt="CyberSmart" className="exempli-img" />
                  </div>
                  <div className="exempli-text cybersmart-text">
                    <p>{t('passion.example4Short')}</p>
                    <Link to="/cybersmart" className="learn-more-link"><em>{t('passion.readMore')}</em></Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassionProject;